import React, { useCallback, useEffect, useState } from "react";
import { legalOrgsService } from "entities/legal_orgs";
import type { ILegalOrgsListParams } from "entities/legal_orgs";
import type { ILegalOrgsFormState } from "../types/legal-orgs-form";

export function useLegalOrgsForm(extUserId: string | null = null): ILegalOrgsFormState {
    const [bisGroups, setBisGroups] = useState<ILegalOrgsFormState["bisGroups"]>([]);
    const [bisUnits, setBisUnits] = useState<ILegalOrgsFormState["bisUnits"]>([]);
    const [countries, setCountries] = useState<ILegalOrgsFormState["countries"]>([]);
    const [orgs, setOrgs] = useState<ILegalOrgsFormState["orgs"]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadInitial = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [groups, units, countryCodes, orgsList] = await Promise.all([
                legalOrgsService.getBisGroups(extUserId),
                legalOrgsService.getBisUnits(extUserId),
                legalOrgsService.getCountries(extUserId),
                legalOrgsService.getOrgs({}, extUserId),
            ]);
            setBisGroups(groups);
            setBisUnits(units);
            setCountries(countryCodes);
            setOrgs(orgsList);
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setBisGroups([]);
            setBisUnits([]);
            setCountries([]);
            setOrgs([]);
        } finally {
            setLoading(false);
        }
    }, [extUserId]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const reloadOrgs = useCallback(async (params?: ILegalOrgsListParams) => {
        try {
            const orgsList = await legalOrgsService.getOrgs(params, extUserId);
            setOrgs(orgsList);
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
        }
    }, [extUserId]);

    return {
        bisGroups,
        bisUnits,
        countries,
        orgs,
        loading,
        error,
        extUserId,
        reloadOrgs,
    };
}
